/**
 * Strategy Execution 헬퍼 함수
 */
import { strategyApi } from '../services/strategyApi';
import { getCurrentWatchlist, refreshWatchlist } from '../services/watchlistApi';
import type { AIStockData } from '../types/watchlist';
import { mapStrategySignalResponse } from '../utils/strategySignals';

const SIGNAL_CONCURRENCY = 3;
const SIGNAL_CACHE_TTL_MS = 60_000;
const LIVE_SIGNAL_DELAY_MS = 1100;
const LIVE_RATE_LIMIT_RETRY_MS = 1200;

type SignalCacheEntry = {
  expiresAt: number;
  strategy_strength?: number;
  strategy_signals: AIStockData['strategy_signals'];
};

const signalCache = new Map<string, SignalCacheEntry>();

/** 테스트 간 모듈 전역 시그널 캐시 격리. */
export function clearSignalCacheForTests(): void {
  signalCache.clear();
}

/** 후보 종목에 전략 신호 추가 */
export async function fetchSignalsForStocks(
  stocks: AIStockData[],
  profile?: string,
): Promise<AIStockData[]> {
  const marked = stocks.map((stock) => ({ ...stock, signal_status: 'loading' as const }));
  if (profile === 'live') return fetchLiveSignalsSequentially(marked, profile);
  return mapWithConcurrency(marked, SIGNAL_CONCURRENCY, (stock) => (
    fetchSignalForStock(stock, profile)
  ));
}

async function fetchSignalForStock(stock: AIStockData, profile?: string): Promise<AIStockData> {
  const cached = getCachedSignal(stock, profile);
  if (cached) return cached;
  try {
    const signal = await getSignalWithRetry(stock.ticker, profile);
    const updated = applySignal(stock, signal);
    setCachedSignal(updated, profile);
    return updated;
  } catch (error: any) {
    console.error(`${stock.ticker} 신호 조회 실패:`, error);
    return {
      ...stock,
      signal_status: 'error' as const,
      signal_error: error.message || '신호 조회 실패',
    };
  }
}

async function fetchLiveSignalsSequentially(stocks: AIStockData[], profile: string) {
  const results: AIStockData[] = [];
  for (const stock of stocks) {
    results.push(await fetchSignalForStock(stock, profile));
    if (results.length < stocks.length) await delay(LIVE_SIGNAL_DELAY_MS);
  }
  return results;
}

async function getSignalWithRetry(ticker: string, profile?: string) {
  try {
    return await strategyApi.getSignal(ticker, profile);
  } catch (error: any) {
    if (profile !== 'live' || !isTossChartRateLimit(error.message || '')) throw error;
    await delay(LIVE_RATE_LIMIT_RETRY_MS);
    return strategyApi.getSignal(ticker, profile);
  }
}

function applySignal(stock: AIStockData, signal: any): AIStockData {
  return {
    ...stock,
    strategy_strength: normalizeSignalStrength(signal),
    strategy_signals: mapStrategySignalResponse(signal),
    signal_status: 'ready',
    signal_error: undefined,
  };
}

function getCachedSignal(stock: AIStockData, profile?: string): AIStockData | null {
  const cached = signalCache.get(cacheKey(stock.ticker, profile));
  if (!cached || cached.expiresAt < Date.now()) return null;
  return {
    ...stock,
    strategy_strength: cached.strategy_strength,
    strategy_signals: cached.strategy_signals,
    signal_status: 'ready',
  };
}

function setCachedSignal(stock: AIStockData, profile?: string): void {
  signalCache.set(cacheKey(stock.ticker, profile), {
    expiresAt: Date.now() + SIGNAL_CACHE_TTL_MS,
    strategy_strength: stock.strategy_strength,
    strategy_signals: stock.strategy_signals,
  });
}

function normalizeSignalStrength(signal: any): number {
  const value = Number(signal?.weighted_total ?? signal?.strength ?? 0);
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, Math.abs(value)));
}

function cacheKey(ticker: string, profile?: string) {
  return `${profile || 'default'}:${ticker}`;
}

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isTossChartRateLimit(errorMsg: string): boolean {
  return errorMsg.includes('MARKET_DATA_CHART') ||
    errorMsg.includes('요청 한도 초과') ||
    errorMsg.includes('429');
}

async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  mapper: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let cursor = 0;
  async function worker() {
    while (cursor < items.length) {
      const index = cursor++;
      results[index] = await mapper(items[index]);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

/** AI Watchlist 조회 */
export async function fetchWatchlistData() {
  const data = await getCurrentWatchlist();
  const recommendations = data.full_data?.recommendations || [];
  return {
    recommendations,
    lastRefresh: data.last_refresh,
    refreshInterval: data.refresh_interval_seconds,
    nextSlotEt: data.refresh_policy?.next_slot_et,
  };
}

/** 수동 새로고침 처리 */
export async function handleRefresh(): Promise<{ count: number; refreshedAt: string }> {
  const result = await refreshWatchlist();
  return { count: result.count, refreshedAt: result.refreshed_at };
}

/** Rate Limit 에러 감지 */
export function isRateLimitError(errorMsg: string): boolean {
  return errorMsg.includes('429') ||
    errorMsg.includes('quota') ||
    errorMsg.includes('Rate Limit') ||
    errorMsg.includes('할당량');
}

/** 전략 수동 실행 */
export async function executeStrategyForSymbol(symbol: string) {
  return await strategyApi.executeStrategy(symbol);
}

/** 실행 로그 조회 */
export async function fetchExecutionLogs(limit: number = 50) {
  return await strategyApi.getLogs(limit);
}
